// Actions
import { ActionType } from "@ms/uno-actions/lib/local/ActionType";
// Constants
import { TaskGroupingState } from "@ms/uno-constants/lib/local/GroupingConstants";
import { GridViewName, TaskGridColumnId } from "@ms/uno-constants/lib/local/TaskGridConstants";
import { LeftNavCollapseThreshold } from "@ms/uno-constants/lib/local/UIConstants";
// Models
import { EntityFilter, Filter } from "@ms/uno-models/lib/local/client/filter/Filter";
import { TaskAccessContext } from "@ms/uno-models/lib/local/client/legacyPlanner/task/TaskAccessContext";
import { LRUCache } from "@ms/uno-models/lib/local/client/lru/LRUCache";
import { PersistedLRUCache } from "@ms/uno-models/lib/local/client/lru/PersistedLRUCache";
import { getTaskAccessContextFromViewKey } from "@ms/uno-models/lib/local/utilities/TaskAccessContextUtilities";
// Stores
import { EventPublishingStore } from "@ms/uno-fluxcore/lib/local/stores/EventPublishingStore";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { LocalIdGenerator } from "@ms/uno-utilities/lib/local/LocalIdGenerator";
import isEqual from "lodash/isEqual";
import { logUnreachableAction } from "../../../utilities/LoggingUtilities";
import isEmpty from "lodash/isEmpty";
export class ViewStore extends EventPublishingStore {
    get name() {
        return "ViewStore";
    }
    getViewContext(accessContext, contextEntityId) {
        let viewContextId = this.indexByAccessContext[accessContext]?.[contextEntityId];
        // Fill the view context if hasn't yet been set for entity id for this given task access context
        if (viewContextId == null) {
            viewContextId = this.setViewContext(accessContext, contextEntityId, false);
        }
        return this.viewContexts[viewContextId];
    }
    getCurrentTaskGroupingState(viewId) {
        const currentTaskGroupingState = this.taskGroupingStateLRUCache.get(viewId);
        if (currentTaskGroupingState == null) {
            return null;
        }
        return currentTaskGroupingState;
    }
    getCurrentTaskFilter(viewKey) {
        const currentTaskFilter = this.taskFilterLRUCache.get(viewKey);
        // If the filter is not found in the cache, return no filter
        if (currentTaskFilter == null) {
            return new EntityFilter([]);
        }
        return currentTaskFilter;
    }
    getCurrentPlanFilter() {
        return this.currentPlanFilter;
    }
    getFilterExceptions() {
        return this.filterExceptions;
    }
    getMyPlansTeamsExpandedState() {
        return this.currentMyPlansTeamsExpandedState;
    }
    getSortCriteria(viewId) {
        const currentSortCriteria = this.sortCriteriaLRUCache.get(viewId);
        if (currentSortCriteria == null) {
            return null;
        }
        return currentSortCriteria;
    }
    getGridColumnWidths(viewId) {
        const columnWidths = this.gridColumnWidthLRUCache.get(viewId);
        if (!columnWidths) {
            return null;
        }
        return columnWidths;
    }
    getLeftNavExpanded() {
        return this.leftNavExpandedState;
    }
    getFABState() {
        return this.FABState;
    }
    getGridColumnOrder(viewId) {
        const taskAccessContext = getTaskAccessContextFromViewKey(viewId);
        if (taskAccessContext === TaskAccessContext.Plan) {
            // Check view settings from plan data
            const orderedColumnsFromPlanData = this.viewSettingsPlanData[viewId]?.gridColumns;
            if (!isEmpty(orderedColumnsFromPlanData)) {
                return orderedColumnsFromPlanData;
            }
        } else {
            // Check view settings from roaming data
            const orderedColumnsFromRoamingData = this.viewSettingsRoamingData[viewId]?.gridColumns;
            if (orderedColumnsFromRoamingData) {
                return orderedColumnsFromRoamingData;
            }
            // Fallback to local storage data
            const data = this.persistentStorage?.getItem(this.getStorageKey(ViewStore.ViewSettingsKey));
            if (data) {
                try {
                    const viewSettings = JSON.parse(data);
                    return viewSettings[viewId]?.gridColumns;
                } catch (e) {
                    this.loggers.traceLogger.logTrace(0x1e141153 /* tag_4fbft */ , TraceLevel.Warning, `Error parsing view settings from local storage [Store=${this.name}]`);
                    return null;
                }
            }
        }
        return null;
    }
    handleAction(action) {
        let storeChanged = false;
        switch(action.type){
            case ActionType.SetCategoryLabelModeAction:
                storeChanged = this.handleSetCategoryLabelModeAction(action);
                break;
            case ActionType.SetTaskFilterAction:
                storeChanged = this.handleSetTaskFilterAction(action);
                break;
            case ActionType.SetTaskGroupingStateAction:
                storeChanged = this.handleSetTaskGroupingStateAction(action);
                storeChanged = this.clearFilterExceptions() || storeChanged;
                break;
            case ActionType.SetTeamsExpandedStateAction:
                storeChanged = this.handleSetTeamsExpandedStateAction(action);
                break;
            case ActionType.SetPlanFilterAction:
                storeChanged = this.handleSetPlanFilterAction(action);
                break;
            case ActionType.NavigateToView:
                storeChanged = this.clearFilterExceptions();
                break;
            case ActionType.CreateBasicTaskAction:
                storeChanged = this.addTaskIdToFilterExceptions(action.taskEntitySet.id);
                break;
            case ActionType.CreateBasicTaskFailedAction:
                storeChanged = this.removeTaskIdFromFilterExceptions(action.taskEntitySet.id);
                break;
            case ActionType.CreateBasicTaskSucceededAction:
                storeChanged = this.handleTaskCreationSuccess(action);
                break;
            case ActionType.UpdateBasicTaskAction:
                storeChanged = this.addTaskIdToFilterExceptions(action.updatedTaskEntitySet.task.id);
                break;
            case ActionType.UpdateBasicTaskFailedAction:
                storeChanged = this.removeTaskIdFromFilterExceptions(action.taskId);
                break;
            case ActionType.DeleteBasicBucketSucceededAction:
                storeChanged = this.removeDeletedBucketFromTaskFilter(action.bucketId);
                break;
            case ActionType.DeltaSyncDeleteBasicBucketAction:
                storeChanged = this.removeDeletedBucketFromTaskFilter(action.bucketId);
                break;
            case ActionType.SetSortCriteriaAction:
                storeChanged = this.handleSetSortCriteriaAction(action);
                break;
            case ActionType.SetGridColumnWidthAction:
                storeChanged = this.handleSetGridColumnWidthAction(action);
                break;
            case ActionType.SetLeftNavExpandedAction:
                storeChanged = this.setLeftNavExpanded(action);
                break;
            case ActionType.SetFABStateAction:
                storeChanged = this.setFABState(action);
                break;
            case ActionType.FetchRoamingUserDataSucceededAction:
                storeChanged = this.handleFetchRoamingUserDataSucceededAction(action);
                break;
            case ActionType.UpdateRoamingUserDataAction:
                storeChanged = this.handleUpdateRoamingUserDataAction(action);
                break;
            case ActionType.UpdateRoamingUserDataSucceededAction:
                storeChanged = this.handleUpdateRoamingUserDataSucceededAction(action);
                break;
            case ActionType.UpdateRoamingUserDataFailedAction:
                storeChanged = this.handleUpdateRoamingUserDataFailedAction();
                break;
            case ActionType.FetchBasicPlanSucceededAction:
                storeChanged = this.handleFetchBasicPlanSucceededAction(action);
                break;
            case ActionType.UpdateBasicPlanAction:
                storeChanged = this.handleUpdateBasicPlanAction(action);
                break;
            case ActionType.UpdateBasicPlanSucceededAction:
                storeChanged = this.handleUpdateBasicPlanSucceededAction(action);
                break;
            case ActionType.UpdateBasicPlanFailedAction:
                storeChanged = this.handleUpdateBasicPlanFailedAction();
                break;
            default:
                logUnreachableAction(this, this.loggers, action);
                return;
        }
        if (storeChanged) {
            this.publishStoreChangedEvent();
        }
    }
    handleSetCategoryLabelModeAction(action) {
        const { accessContext, isCollapsed, contextEntityId } = action;
        const viewContextId = this.indexByAccessContext[accessContext]?.[contextEntityId];
        if (viewContextId == null) {
            this.setViewContext(accessContext, contextEntityId, isCollapsed);
            return true;
        } else if (this.viewContexts[viewContextId].isLabelCollapsed !== isCollapsed) {
            this.viewContexts[viewContextId] = {
                isLabelCollapsed: isCollapsed
            };
            return true;
        }
        return false;
    }
    handleSetTaskGroupingStateAction(action) {
        const { viewId, groupingState } = action;
        if (!isEqual(groupingState, this.taskGroupingStateLRUCache.get(viewId))) {
            this.taskGroupingStateLRUCache.set(viewId, groupingState);
            return true;
        }
        return false;
    }
    handleSetTaskFilterAction(action) {
        const { viewKey, filter } = action;
        if (!isEqual(filter, this.taskFilterLRUCache.get(viewKey))) {
            this.taskFilterLRUCache.set(viewKey, filter);
            this.filterExceptions = {};
            return true;
        }
        return false;
    }
    handleTaskCreationSuccess(action) {
        const localId = action.localId;
        const taskId = action.taskEntitySet.id;
        // Update filterExceptions list by replacing localId with task id returned by service
        const count = this.filterExceptions[localId];
        if (count) {
            this.filterExceptions[taskId] = count;
            delete this.filterExceptions[localId];
            return true;
        }
        return false;
    }
    /**
     * Handle the action to set the expanded state of a team in the my plans view
     * @param action Action to set the expanded state of a team
     */ handleSetTeamsExpandedStateAction(action) {
        const { teamId, isExpanded } = action;
        if (this.currentMyPlansTeamsExpandedState[teamId] !== isExpanded) {
            this.currentMyPlansTeamsExpandedState[teamId] = isExpanded;
            this.persistentStorage?.setItem(this.getStorageKey(ViewStore.TeamsExpandedStateFieldKey), JSON.stringify(this.currentMyPlansTeamsExpandedState));
            return true;
        }
        return false;
    }
    /**
     * Handle the action to set the input state of the keyword filter in My Plans view
     * @param action Action to set the keyword filter
     */ handleSetPlanFilterAction(action) {
        const { filter } = action;
        if (!isEqual(filter, this.currentPlanFilter)) {
            this.currentPlanFilter = filter;
            return true;
        }
        return false;
    }
    /**
     * Clear all the filter exceptions
     */ clearFilterExceptions() {
        if (Object.keys(this.filterExceptions).length > 0) {
            this.filterExceptions = {};
            return true;
        }
        return false;
    }
    /**
     * Add task id to filter exceptions list
     * @param taskId Task id
     */ addTaskIdToFilterExceptions(taskId) {
        let needToUpdate = false;
        for (const key of this.taskFilterLRUCache.keys()){
            const taskFilters = this.taskFilterLRUCache.get(key);
            if (taskFilters && taskFilters.filters.length > 0) {
                needToUpdate = true;
            }
        }
        if (needToUpdate) {
            // If taskId does not exists in the list, add an entry for it and update the value to 1
            // If taskId exists in the list, update the entry by incrementing the value
            let value = this.filterExceptions[taskId];
            this.filterExceptions[taskId] = value ? ++value : 1;
            return true;
        }
        return false;
    }
    /**
     * Remove task id from filter exceptions list
     * @param taskId Task id
     */ removeTaskIdFromFilterExceptions(taskId) {
        // Decrement the value of taskID in the list and delete the entry if value is 0.
        // We need to keep track of the value to avoid losing information when create/update operation is performed multiple times in the filtered view.
        if (this.filterExceptions[taskId]) {
            this.filterExceptions[taskId]--;
            if (this.filterExceptions[taskId] === 0) {
                delete this.filterExceptions[taskId];
            }
            return true;
        }
        return false;
    }
    /**
     * Remove the bucket id from the task entity filter if present
     * @param bucketId Id of the bucket
     */ removeDeletedBucketFromTaskFilter(bucketId) {
        let storeChanged = false;
        for (const key of this.taskFilterLRUCache.keys()){
            const taskFilters = this.taskFilterLRUCache.get(key);
            if (taskFilters?.doesFilterValueExist("bucketId", bucketId)) {
                const newFilter = taskFilters.toggleFilterValue("bucketId", bucketId);
                this.taskFilterLRUCache.set(key, newFilter);
                storeChanged = true;
            }
        }
        return storeChanged;
    }
    /**
     * Handle set sort criteria action
     * @param action Action fired when set sortCriteria
     */ handleSetSortCriteriaAction(action) {
        const { viewId, sortCriteria } = action;
        if (sortCriteria == null) {
            this.sortCriteriaLRUCache.delete(viewId);
            return true;
        }
        if (!isEqual(sortCriteria, this.sortCriteriaLRUCache.get(viewId))) {
            this.sortCriteriaLRUCache.set(viewId, sortCriteria);
            return true;
        }
        return false;
    }
    /**
     * Handle set grid column width action
     * @param action Action fired when set grid column width
     */ handleSetGridColumnWidthAction(action) {
        const { viewId, columnId, columnWidth } = action;
        // Get the current column widths for the view
        const columnWidths = this.gridColumnWidthLRUCache.get(viewId) ?? {};
        if (columnWidths[columnId] === columnWidth) {
            return false;
        }
        // Update with the new column width
        columnWidths[columnId] = columnWidth;
        this.gridColumnWidthLRUCache.set(viewId, columnWidths);
        return true;
    }
    /**
     * Handle fetch roaming user data succeeded action
     * @param action Action fired when fetch roaming user data succeeded
     */ handleFetchRoamingUserDataSucceededAction(action) {
        const { roamingUserData } = action;
        this.viewSettingsRoamingData = roamingUserData.viewSettings;
        return true;
    }
    /**
     * Handle update roaming user data action
     * @param action Action fired when update roaming user data
     */ handleUpdateRoamingUserDataAction(action) {
        const { roamingUserData } = action;
        // Store the previous settings
        this.previousViewSettingsRoamingData = {
            ...this.viewSettingsRoamingData
        };
        // Update view settings optimistically
        this.viewSettingsRoamingData = roamingUserData.viewSettings;
        // Save roaming data to local storage
        this.persistentStorage?.setItem(this.getStorageKey(ViewStore.ViewSettingsKey), JSON.stringify(this.viewSettingsRoamingData));
        return true;
    }
    /**
     * Handle update roaming user data succeeded action
     * @param action Action fired when update roaming user data succeeded
     */ handleUpdateRoamingUserDataSucceededAction(action) {
        const { roamingUserData } = action;
        // Clear the previous setting since the update succeeded
        this.previousViewSettingsRoamingData = null;
        // Check whether the view settings need to be updated
        if (!isEqual(this.viewSettingsRoamingData, roamingUserData.viewSettings)) {
            this.viewSettingsRoamingData = roamingUserData.viewSettings;
            // Update the view settings in local storage
            this.persistentStorage?.setItem(this.getStorageKey(ViewStore.ViewSettingsKey), JSON.stringify(this.viewSettingsRoamingData));
            return true;
        }
        return false;
    }
    /**
     * Handle update roaming user data failed action
     */ handleUpdateRoamingUserDataFailedAction() {
        // Revert back to the previous settings
        if (this.previousViewSettingsRoamingData) {
            this.viewSettingsRoamingData = {
                ...this.previousViewSettingsRoamingData
            };
            // Update the view settings in local storage
            this.persistentStorage?.setItem(this.getStorageKey(ViewStore.ViewSettingsKey), JSON.stringify(this.viewSettingsRoamingData));
            this.previousViewSettingsRoamingData = null;
            return true;
        }
        return false;
    }
    /**
     * Handle fetch basic plan succeeded action
     * @param action Action fired when fetch basic plan succeeded
     */ handleFetchBasicPlanSucceededAction(action) {
        const { planEntitySet } = action;
        const gridSettings = planEntitySet.plan.internalProperties.appProperties?.plannerclient?.gridSettings;
        if (gridSettings) {
            const viewId = `${GridViewName.BasicPlanTasksGridView}_${planEntitySet.id}`;
            const storeGridSettings = this.viewSettingsPlanData[viewId];
            if (!isEqual(gridSettings, storeGridSettings)) {
                this.viewSettingsPlanData = {
                    ...this.viewSettingsPlanData,
                    [viewId]: gridSettings
                };
                return true;
            }
        }
        return false;
    }
    /**
     * Handle update basic plan action
     * @param action Action fired when update basic plan
     */ handleUpdateBasicPlanAction(action) {
        const { planDelta, planId } = action;
        // Check if the view settings are being updated
        if (planDelta != null) {
            const updatedGridSettings = planDelta.internalProperties?.appProperties?.plannerclient?.gridSettings;
            if (updatedGridSettings) {
                const viewId = `${GridViewName.BasicPlanTasksGridView}_${planId}`;
                const storeGridSettings = this.viewSettingsPlanData[viewId];
                if (!isEqual(updatedGridSettings, storeGridSettings)) {
                    // Store the previous settings
                    this.previousViewSettingsPlanData = {
                        ...this.viewSettingsPlanData
                    };
                    // Update view settings optimistically
                    this.viewSettingsPlanData = {
                        ...this.viewSettingsPlanData,
                        [viewId]: updatedGridSettings
                    };
                    return true;
                }
            }
        }
        return false;
    }
    /**
     * Handle update basic plan succeeded action
     * @param action Action fired when update basic plan succeeded
     */ handleUpdateBasicPlanSucceededAction(action) {
        const { updatedPlan, planId } = action;
        // Clear the previous setting since the update succeeded
        this.previousViewSettingsPlanData = null;
        const gridSettings = updatedPlan?.internalProperties.appProperties?.plannerclient?.gridSettings;
        if (gridSettings) {
            const viewId = `${GridViewName.BasicPlanTasksGridView}_${planId}`;
            const storeGridSettings = this.viewSettingsPlanData[viewId];
            if (!isEqual(gridSettings, storeGridSettings)) {
                this.viewSettingsPlanData = {
                    ...this.viewSettingsPlanData,
                    [viewId]: gridSettings
                };
                return true;
            }
        }
        return false;
    }
    /**
     * Handle update basic plan failed action
     */ handleUpdateBasicPlanFailedAction() {
        // Revert back to the previous settings
        if (this.previousViewSettingsPlanData) {
            this.viewSettingsPlanData = {
                ...this.previousViewSettingsPlanData
            };
            this.previousViewSettingsPlanData = null;
            return true;
        }
        return false;
    }
    /**
     *
     * @param accessContext Access context to set labels collapsed state for
     * @param contextEntityId Id of the entity in the given access context
     * @param isLabelCollapsed Whether the labels should be set to collapsed
     */ setViewContext(accessContext, contextEntityId, isLabelCollapsed) {
        // Generate a new view context id
        const viewContextId = LocalIdGenerator.generate();
        // Create a new view context
        const viewContext = {
            isLabelCollapsed
        };
        // Add to container
        this.viewContexts[viewContextId] = viewContext;
        // Add to index
        this.indexByAccessContext[accessContext][contextEntityId] = viewContextId;
        return viewContextId;
    }
    /**
     * Set the left nav expanded state
     * @param action Action to set the left nav expanded state
     */ setLeftNavExpanded(action) {
        if (this.leftNavExpandedState !== action.isExpanded) {
            this.leftNavExpandedState = action.isExpanded;
            return true;
        }
        return false;
    }
    /**
     * Set the FAB mode
     * @param action Action to set the FAB mode
     */ setFABState(action) {
        if (!isEqual(this.FABState, action.state)) {
            this.FABState = action.state;
            return true;
        }
        return false;
    }
    /**
     * Retrieves data from persistent storage and hydrates store
     */ hydrateFromPersistentStorage() {
        if (!this.persistentStorage) {
            return;
        }
        // Hydrate Teams expanded states
        const storageKey = this.getStorageKey(ViewStore.TeamsExpandedStateFieldKey);
        const persistedTeamsExpandedState = this.persistentStorage.getItem(storageKey);
        if (persistedTeamsExpandedState != null) {
            try {
                this.currentMyPlansTeamsExpandedState = JSON.parse(persistedTeamsExpandedState);
                this.loggers.traceLogger.logTrace(0x1e1d84e3 /* tag_4hyt9 */ , TraceLevel.Info, `Hydrated teams expanded state [Store=${this.name}][Key=${storageKey}]`);
            } catch (e) {
                this.loggers.traceLogger.logTrace(0x1e1d84e2 /* tag_4hyt8 */ , TraceLevel.Warning, `Hydration error [Store=${this.name}][Key=${storageKey}]`);
            }
        }
        // Hydrate task filter LRU cache
        const taskFiltersStorageKey = this.getStorageKey(ViewStore.TaskFiltersKey);
        const persistedTaskFilters = this.persistentStorage.getItem(taskFiltersStorageKey);
        if (persistedTaskFilters != null) {
            try {
                const taskFilters = JSON.parse(persistedTaskFilters);
                // JSON.parse returns a plain object for the taskFilter value
                // We need to convert into an EntityFilter before loading into the LRU cache
                const convertedTaskFilters = taskFilters.map((taskFilter)=>{
                    // Convert filters into Filter objects
                    const convertedFilters = taskFilter.v.filters.map((filter)=>{
                        return new Filter(filter.field, filter.values);
                    });
                    return {
                        ...taskFilter,
                        v: new EntityFilter(convertedFilters)
                    };
                });
                this.taskFilterLRUCache.load(convertedTaskFilters);
                this.loggers.traceLogger.logTrace(0x1e1d84e1 /* tag_4hyt7 */ , TraceLevel.Info, `Hydrated task filters [Store=${this.name}][Key=${taskFiltersStorageKey}]`);
            } catch (e) {
                this.loggers.traceLogger.logTrace(0x1e1d84e0 /* tag_4hyt6 */ , TraceLevel.Warning, `Hydration error [Store=${this.name}][Key=${taskFiltersStorageKey}]`);
            }
        }
        // Hydrate sort criteria LRU cache
        const sortCriteriaStorageKey = this.getStorageKey(ViewStore.SortCriteriaKey);
        const persistedSortCriteria = this.persistentStorage.getItem(sortCriteriaStorageKey);
        if (persistedSortCriteria != null) {
            try {
                const sortCriteria = JSON.parse(persistedSortCriteria);
                // Validate that primary field is a TaskGridColumnId
                const validatedSortCriteria = sortCriteria.reduce((result, sortCriteria)=>{
                    const primaryField = sortCriteria.v.primary.field;
                    if (this.isValidTaskGridColumnId(primaryField)) {
                        result.push(sortCriteria);
                    }
                    return result;
                }, []);
                this.sortCriteriaLRUCache.load(validatedSortCriteria);
                this.loggers.traceLogger.logTrace(0x1e1d84df /* tag_4hyt5 */ , TraceLevel.Info, `Hydrated sort criteria [Store=${this.name}][Key=${sortCriteriaStorageKey}]`);
            } catch (e) {
                this.loggers.traceLogger.logTrace(0x1e1d84de /* tag_4hyt4 */ , TraceLevel.Warning, `Hydration error [Store=${this.name}][Key=${sortCriteriaStorageKey}]`);
            }
        }
        // Hydrate grid column width LRU cache
        const gridColumnWidthStorageKey = this.getStorageKey(ViewStore.GridColumnWidthKey);
        const persistedGridColumnWidths = this.persistentStorage.getItem(gridColumnWidthStorageKey);
        if (persistedGridColumnWidths != null) {
            try {
                const gridColumnWidths = JSON.parse(persistedGridColumnWidths);
                // Validate that columnId is a TaskGridColumnId for the view column widths
                const validatedGridColumnWidths = gridColumnWidths.reduce((result, viewColumnWidths)=>{
                    const columnWidths = viewColumnWidths.v;
                    const validatedColumnWidths = {};
                    Object.keys(columnWidths).forEach((columnId)=>{
                        if (this.isValidTaskGridColumnId(columnId)) {
                            validatedColumnWidths[columnId] = columnWidths[columnId];
                        }
                    });
                    const validatedViewColumnWidth = {
                        ...viewColumnWidths,
                        v: validatedColumnWidths
                    };
                    result.push(validatedViewColumnWidth);
                    return result;
                }, []);
                this.gridColumnWidthLRUCache.load(validatedGridColumnWidths);
                this.loggers.traceLogger.logTrace(0x1e1d84dd /* tag_4hyt3 */ , TraceLevel.Info, `Hydrated grid column widths [Store=${this.name}][Key=${gridColumnWidthStorageKey}]`);
            } catch (e) {
                this.loggers.traceLogger.logTrace(0x1e1d84dc /* tag_4hyt2 */ , TraceLevel.Warning, `Hydration error [Store=${this.name}][Key=${gridColumnWidthStorageKey}]`);
            }
        }
        // Hydrate task grouping state LRU cache
        const taskGroupingStateStorageKey = this.getStorageKey(ViewStore.TaskGroupingStateKey);
        const persistedTaskGroupingState = this.persistentStorage.getItem(taskGroupingStateStorageKey);
        if (persistedTaskGroupingState != null) {
            try {
                const taskGroupingStates = JSON.parse(persistedTaskGroupingState);
                const validatedViewTaskGroupingStates = taskGroupingStates.reduce((result, viewTaskGroupingState)=>{
                    const taskGroupingState = viewTaskGroupingState.v;
                    if (TaskGroupingState[taskGroupingState] != null) {
                        result.push(viewTaskGroupingState);
                    }
                    return result;
                }, []);
                this.taskGroupingStateLRUCache.load(validatedViewTaskGroupingStates);
                this.loggers.traceLogger.logTrace(0x1e1d84db /* tag_4hyt1 */ , TraceLevel.Info, `Hydrated task grouping states [Store=${this.name}][Key=${taskGroupingStateStorageKey}]`);
            } catch (e) {
                this.loggers.traceLogger.logTrace(0x1e1d84da /* tag_4hyt0 */ , TraceLevel.Warning, `Hydration error [Store=${this.name}][Key=${taskGroupingStateStorageKey}]`);
            }
        }
    }
    /**
     * Helper to validate if the columnId is a TaskGridColumnId
     * @param columnId String to validate
     */ isValidTaskGridColumnId(columnId) {
        return Object.values(TaskGridColumnId).includes(columnId);
    }
    /**
     * Get the viewStore storage key for the field
     * @param fieldKey The field key
     */ getStorageKey(fieldKey) {
        return `${ViewStore.ViewStoreKey}-${fieldKey}`;
    }
    /**
     * Initialize a new instance of the ViewStore
     * @param loggers The loggers for telemetry
     * @param persistentStorage The persistent storage to use
     */ constructor(loggers, persistentStorage, configProvider){
        super(), this.loggers = loggers, this.persistentStorage = persistentStorage, this.configProvider = configProvider, this.viewContexts = {}, this.currentPlanFilter = new EntityFilter([]), this.filterExceptions = {}, this.currentMyPlansTeamsExpandedState = {}, this.FABState = {
            mode: "collapsed"
        }, this.viewSettingsRoamingData = {}, this.previousViewSettingsRoamingData = null, this.viewSettingsPlanData = {}, this.previousViewSettingsPlanData = null, this.indexByAccessContext = {
            [TaskAccessContext.Plan]: {},
            [TaskAccessContext.AssignedToUser]: {},
            [TaskAccessContext.MyDay]: {}
        };
        if (this.persistentStorage) {
            this.taskFilterLRUCache = new PersistedLRUCache(this.persistentStorage, this.getStorageKey(ViewStore.TaskFiltersKey), this.configProvider().settings.persistedStorage.limits.maxTaskFilters);
        } else {
            this.taskFilterLRUCache = new LRUCache(this.configProvider().settings.persistedStorage.limits.maxTaskFilters);
        }
        if (this.persistentStorage) {
            this.sortCriteriaLRUCache = new PersistedLRUCache(this.persistentStorage, this.getStorageKey(ViewStore.SortCriteriaKey), this.configProvider().settings.persistedStorage.limits.maxSortCriteria);
        } else {
            this.sortCriteriaLRUCache = new LRUCache(this.configProvider().settings.persistedStorage.limits.maxSortCriteria);
        }
        if (this.persistentStorage) {
            this.gridColumnWidthLRUCache = new PersistedLRUCache(this.persistentStorage, this.getStorageKey(ViewStore.GridColumnWidthKey), this.configProvider().settings.persistedStorage.limits.maxGridColumnWidths);
        } else {
            this.gridColumnWidthLRUCache = new LRUCache(this.configProvider().settings.persistedStorage.limits.maxGridColumnWidths);
        }
        if (this.persistentStorage) {
            this.taskGroupingStateLRUCache = new PersistedLRUCache(this.persistentStorage, this.getStorageKey(ViewStore.TaskGroupingStateKey), this.configProvider().settings.persistedStorage.limits.maxTaskGroupings);
        } else {
            this.taskGroupingStateLRUCache = new LRUCache(this.configProvider().settings.persistedStorage.limits.maxTaskGroupings);
        }
        // Initialize left nav expanded state based on window width.
        // Do not collapse if window width is 0 (Happens when view port is set to 0 width when cached within Teams).
        this.leftNavExpandedState = window.innerWidth === 0 || window.innerWidth > LeftNavCollapseThreshold;
        this.hydrateFromPersistentStorage();
    }
}
ViewStore.ViewStoreKey = "vstore";
ViewStore.TeamsExpandedStateFieldKey = "tes";
ViewStore.TaskFiltersKey = "tf";
ViewStore.SortCriteriaKey = "sc";
ViewStore.GridColumnWidthKey = "gcw";
ViewStore.ViewSettingsKey = "vs";
ViewStore.TaskGroupingStateKey = "tgs";
